import type { SavedViewConfig } from "@/components/shared/UniversalCRMTable/types";
import type { TicketFieldId } from "../fields/ticket-field-ids";
import { ticketFields } from "../fields";
import { collectFieldsForSavedView } from "./collect-fields-for-saved-view";

/**
 * Ticket Saved Views
 *
 * `visibleColumns` is derived from each field's `savedViewMembership`
 * declaration (see `../fields/*-fields.ts`). Adding/removing a column from a
 * saved view is therefore a single-file edit on the relevant field bundle —
 * no need to keep these arrays in sync by hand.
 */
const fieldsById = (viewId: string): TicketFieldId[] =>
  collectFieldsForSavedView<TicketFieldId>(viewId, ticketFields);

/**
 * Status values that count as "still needs work" — everything except
 * resolved / closed / cancelled. Drives the one-click Unresolved view.
 */
const UNRESOLVED_STATUSES = [
  "new",
  "assigned",
  "in_progress",
  "waiting_for_customer",
  "waiting_for_vendor",
];

// The follow-up views (Unresolved / Pending / Needs First Response / Stale) all
// reuse the Open Tickets column set — same triage columns, different filter.
const triageColumns = (): TicketFieldId[] => fieldsById("open-tickets");

export const ticketSavedViews: SavedViewConfig<TicketFieldId>[] = [
  {
    id: "all-tickets",
    name: "All Tickets",
    filters: {},
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: fieldsById("all-tickets"),
  },
  {
    id: "my-tickets",
    name: "My Tickets",
    filters: { assigned_to: "me" },
    sort: { field: "due_date", direction: "asc" },
    visibleColumns: fieldsById("my-tickets"),
  },
  {
    id: "high-priority",
    name: "High Priority",
    filters: { priority: ["high", "urgent", "critical"] },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: fieldsById("high-priority"),
  },
  {
    id: "open-tickets",
    name: "Open Tickets",
    filters: { status: ["new", "assigned", "in_progress"] },
    sort: { field: "priority", direction: "asc" },
    visibleColumns: fieldsById("open-tickets"),
  },
  {
    // One-click "what still needs work" — everything not resolved/closed/cancelled.
    id: "unresolved-tickets",
    name: "Unresolved",
    filters: { status: UNRESOLVED_STATUSES },
    sort: { field: "priority", direction: "asc" },
    visibleColumns: triageColumns(),
  },
  {
    // "Ball in their court" — waiting on the customer or a vendor.
    id: "pending-tickets",
    name: "Pending",
    filters: { status: ["waiting_for_customer", "waiting_for_vendor"] },
    sort: { field: "updated_at", direction: "asc" },
    visibleColumns: triageColumns(),
  },
  {
    // Open tickets nobody has picked up yet (no assignee). Token handled by the
    // `assigned_to` field's toGraphQLFilter.
    id: "needs-first-response-tickets",
    name: "Needs First Response",
    filters: { assigned_to: "needs-first-response" },
    sort: { field: "created_at", direction: "asc" },
    visibleColumns: triageColumns(),
  },
  {
    // Open tickets with no activity in 24h+ — mirrors the staleness cron signal.
    // Token handled by the `updated_at` field's toGraphQLFilter.
    id: "stale-tickets",
    name: "Stale (24h+)",
    filters: { updated_at: "stale24" },
    sort: { field: "updated_at", direction: "asc" },
    visibleColumns: triageColumns(),
  },
  {
    id: "overdue-tickets",
    name: "Overdue Tickets",
    filters: { due_date: "overdue" },
    sort: { field: "due_date", direction: "asc" },
    visibleColumns: fieldsById("overdue-tickets"),
  },
  {
    id: "resolved-tickets",
    name: "Resolved Tickets",
    filters: { status: ["resolved", "closed"] },
    sort: { field: "resolved_at", direction: "desc" },
    visibleColumns: fieldsById("resolved-tickets"),
  },
  {
    id: "no-time-entries",
    name: "No Time Entries",
    filters: { has_time_entries: false },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: fieldsById("no-time-entries"),
  },
  {
    id: "with-time-entries",
    name: "With Time Entries",
    filters: { has_time_entries: true },
    sort: { field: "created_at", direction: "desc" },
    visibleColumns: fieldsById("with-time-entries"),
  },
];
